import React, { useState, useRef, useEffect } from "react";
import { Rnd } from "react-rnd";

// Define default video dimensions and minimum size constraints
const DEFAULT_VIDEO_WIDTH = 320;
const DEFAULT_VIDEO_HEIGHT = 240;
const MIN_VIDEO_WIDTH = 160;
const MIN_VIDEO_HEIGHT = 120;

const isMobile = window.innerWidth <= 768;

const MultiVideoPlayer = () => {
  const [videos, setVideos] = useState([]);
  const [videoUrl, setVideoUrl] = useState("");
  // Initially show input if no videos are loaded from URL or storage
  const [showInput, setShowInput] = useState(false);
  const [theaterMode, setTheaterMode] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [copiedMessage, setCopiedMessage] = useState(""); // State for "Copied!" message

  const fileInputRef = useRef(null);
  const appContainerRef = useRef(null); // Ref for the main app container to get bounds

  // Effect to load videos from URL on initial mount
  useEffect(() => {
    const loadVideosFromUrl = () => {
      const params = new URLSearchParams(window.location.search);
      const base64Data = params.get("data");

      if (base64Data) {
        try {
          const jsonString = atob(base64Data); // Decode Base64
          const loadedVideoData = JSON.parse(jsonString); // Parse JSON

          if (Array.isArray(loadedVideoData)) {
            const newVideos = loadedVideoData
              .map((videoData) => {
                // Assign new IDs and default position/size for loaded videos
                const defaultVideoWidth = DEFAULT_VIDEO_WIDTH;
                const defaultVideoHeight = DEFAULT_VIDEO_HEIGHT;
                const centeredPosition = getCenteredPosition(
                  defaultVideoWidth,
                  defaultVideoHeight
                );

                return {
                  id: Date.now() + Math.random().toString(36).substr(2, 9),
                  type: videoData.type || "direct", // Default to 'direct' if type is missing
                  url: videoData.url,
                  name:
                    videoData.name ||
                    `Shared Video (${videoData.type || "direct"})`, // Use name from data or create a default
                  position: centeredPosition, // Start centered
                  size: {
                    width: defaultVideoWidth,
                    height: defaultVideoHeight,
                  }, // Start with default size
                };
              })
              .filter((video) => video.url); // Filter out entries without a valid URL

            if (newVideos.length > 0) {
              setVideos(newVideos);
              setShowInput(false); // Hide input if videos are loaded from URL
            } else {
              // If base64 data was present but resulted in no valid videos
              setShowInput(true);
            }
          } else {
            console.error("Loaded data is not an array:", loadedVideoData);
            setShowInput(true); // Show input if data is malformed
          }
        } catch (e) {
          console.error("Failed to decode or parse video data from URL:", e);
          alert(
            "Failed to load videos from URL. The shared link might be invalid."
          );
          setShowInput(true); // Show input on error
        } finally {
          // Clean the URL after attempting to load
          window.history.replaceState(
            {},
            document.title,
            window.location.pathname
          );
        }
      } else {
        // If no 'data' param, show input if videos array is empty initially
        // setShowInput(videos.length === 0);
      }
    };

    loadVideosFromUrl();
    // Effect should only run once on mount
  }); // Empty dependency array means this runs only on mount

  // Track fullscreen state changes
  useEffect(() => {
    const handleFullscreenChange = () => {
      setIsFullscreen(!!document.fullscreenElement);
    };

    document.addEventListener("fullscreenchange", handleFullscreenChange);
    return () => {
      document.removeEventListener("fullscreenchange", handleFullscreenChange);
    };
  }, []);

  // Handle browser history for theater mode
  useEffect(() => {
    const handlePopState = (event) => {
      if (!event.state || !event.state.theaterMode) {
        setTheaterMode(false);
      }
    };

    if (theaterMode) {
      window.history.pushState({ theaterMode: true }, "Theater Mode");
      window.addEventListener("popstate", handlePopState);
    } else {
      window.removeEventListener("popstate", handlePopState);
      if (window.history.state && window.history.state.theaterMode) {
        window.history.replaceState({}, "");
      }
    }

    return () => {
      window.removeEventListener("popstate", handlePopState);
    };
  }, [theaterMode]);

  // Effect to show input if all videos are removed *after* initial load
  // useEffect(() => {
  //   if (videos.length === 0 && !showInput) {
  //     setShowInput(true);
  //   }
  // }, [videos.length, showInput]); // Depend on videos.length and showInput

  // Calculate the initial position to center a new video
  const getCenteredPosition = (videoWidth, videoHeight) => {
    if (appContainerRef.current) {
      const parentRect = appContainerRef.current.getBoundingClientRect();
      const centerX = (parentRect.width - videoWidth) / 2;
      const centerY = (parentRect.height - videoHeight) / 2;
      const clampedX = Math.max(
        0,
        Math.min(centerX, parentRect.width - videoWidth)
      );
      const clampedY = Math.max(
        0,
        Math.min(centerY, parentRect.height - videoHeight)
      );
      return { x: clampedX, y: clampedY };
    }
    return {
      x: window.innerWidth / 2 - videoWidth / 2,
      y: window.innerHeight / 2 - videoHeight / 2,
    };
  };

  // Handle local file selection
  const handleFileChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    const newVideos = files.map((file) => {
      const defaultVideoWidth = DEFAULT_VIDEO_WIDTH;
      const defaultVideoHeight = DEFAULT_VIDEO_HEIGHT;
      const centeredPosition = getCenteredPosition(
        defaultVideoWidth,
        defaultVideoHeight
      );

      return {
        id: Date.now() + Math.random().toString(36).substr(2, 9),
        type: "file",
        url: URL.createObjectURL(file),
        name: file.name,
        position: centeredPosition,
        size: { width: defaultVideoWidth, height: defaultVideoHeight },
      };
    });

    setVideos((prev) => [...prev, ...newVideos]);
    if (newVideos.length > 0) {
      setShowInput(false);
    }
  };

  // Process video URL to get embed format
  const processVideoUrl = (url) => {
    let videoType = "direct";
    let processedUrl = url;
    let videoName = `Video from URL`;

    try {
      const urlObj = new URL(url);
      videoName = `Video from ${urlObj.hostname}`;

      if (url.includes("youtube.com/watch") || url.includes("youtu.be")) {
        videoType = "youtube";
        const videoId = url.includes("youtube.com/watch")
          ? urlObj.searchParams.get("v")
          : urlObj.pathname.split("/").pop();

        if (videoId) {
          processedUrl = `https://www.youtube.com/embed/${videoId}?autoplay=0`;
          videoName = `YouTube Video`;
        } else {
          throw new Error("Could not extract YouTube video ID");
        }
      } else if (url.includes("vimeo.com")) {
        videoType = "vimeo";
        const vimeoId = urlObj.pathname.split("/").pop();
        if (vimeoId) {
          processedUrl = `https://player.vimeo.com/video/${vimeoId}?autoplay=0`;
          videoName = `Vimeo Video`;
        } else {
          throw new Error("Could not extract Vimeo video ID");
        }
      } else if (url.includes("pornhub.com")) {
        videoType = "pornhub";
        let viewkey = "";
        if (url.includes("view_video.php")) {
          viewkey = urlObj.searchParams.get("viewkey");
        } else {
          const matches = urlObj.pathname.match(/\/view\/(\w+)/);
          if (matches && matches[1]) {
            viewkey = matches[1];
          }
        }
        if (viewkey) {
          processedUrl = `https://www.pornhub.com/embed/${viewkey}`;
          videoName = `Pornhub Video`;
        } else {
          throw new Error("Could not extract Pornhub viewkey");
        }
      } else if (url.includes("xvideos.com")) {
        videoType = "xvideos";
        const matches = urlObj.pathname.match(/\/video(\d+)\//);
        if (matches && matches[1]) {
          processedUrl = `https://www.xvideos.com/embedframe/${matches[1]}`;
          videoName = `Xvideos Video`;
        } else {
          throw new Error("Could not extract Xvideos ID");
        }
      } else if (url.includes("dailymotion.com")) {
        videoType = "dailymotion";
        let videoId = "";
        const pathSegments = urlObj.pathname.split("/");
        const videoIndex = pathSegments.indexOf("video");
        const embedIndex = pathSegments.indexOf("embed");

        if (videoIndex !== -1 && videoIndex + 1 < pathSegments.length) {
          videoId = pathSegments[videoIndex + 1];
        } else if (
          embedIndex !== -1 &&
          embedIndex + 2 < pathSegments.length &&
          pathSegments[embedIndex + 1] === "video"
        ) {
          videoId = pathSegments[embedIndex + 2];
        }

        if (videoId) {
          processedUrl = `https://www.dailymotion.com/embed/video/${videoId}?autoplay=0`;
          videoName = `Dailymotion Video`;
        } else {
          throw new Error("Could not extract Dailymotion video ID");
        }
      } else if (url.includes("twitch.tv")) {
        videoType = "twitch";
        const path = urlObj.pathname.split("/");
        if (path[1] === "videos" && path[2]) {
          const videoId = path[2];
          processedUrl = `https://player.twitch.tv/?video=${videoId}&parent=${window.location.hostname}&autoplay=false`;
          videoName = `Twitch VOD`;
        } else if (path[1] && path[1] !== "") {
          const channel = path[1];
          processedUrl = `https://player.twitch.tv/?channel=${channel}&parent=${window.location.hostname}&autoplay=false`;
          videoName = `Twitch Channel`;
        } else {
          throw new Error("Could not extract Twitch video or channel");
        }
      }
    } catch (e) {
      console.error("Error processing URL", e);
      videoType = "direct";
      processedUrl = url;
      videoName = `Direct Link Video`;
      alert(
        `Warning: Could not automatically detect video type for "${url}". Attempting to load as a direct video. Embedding external websites can be blocked by browser security policies.`
      );
    }

    if (
      !processedUrl.startsWith("http://") &&
      !processedUrl.startsWith("https://") &&
      !processedUrl.startsWith("blob:")
    ) {
      // Only throw error if it's not a file URL, as file URLs use blob:
      if (videoType !== "file") {
        throw new Error("Invalid URL format. Must start with http or https.");
      }
    }

    return { type: videoType, url: processedUrl, name: videoName };
  };

  // Add a video from URL
  // Accepts an optional URL so we don't rely on state being updated synchronously
  const addVideo = (inputUrl) => {
    try {
      const raw = typeof inputUrl === "string" ? inputUrl : videoUrl;
      const urlToAdd = (raw || "").trim();

      if (!urlToAdd) {
        alert("Please enter a video URL.");
        return;
      }

      const { type, url, name } = processVideoUrl(urlToAdd);

      const defaultWidth = 480;
      const defaultHeight = 270;
      const { x, y } = getCenteredPosition(defaultWidth, defaultHeight);

      const newVideo = {
        id: Date.now(),
        type,
        url,
        name,
        position: { x, y },
        size: { width: defaultWidth, height: defaultHeight },
      };

      setVideos((prev) => [...prev, newVideo]);

      // Clear input after successful add
      setVideoUrl("");
      // If you previously hid the input based on first video, keep that behavior here if needed
      // if (videos.length === 0) setShowInput(false);
    } catch (e) {
      console.error("Error adding video:", e);
      alert(`Error adding video: ${e.message}`);
    }
  };
  // Toggle input panel visibility
  const toggleInputPanel = () => {
    setShowInput((prev) => !prev);
  };

  // Toggle theater mode
  const toggleTheaterMode = () => {
    setTheaterMode((prev) => !prev);
    // if mobile also toggle fullscreen
    if (isMobile) {
      toggleFullscreen();
    }
  };

  // Toggle fullscreen mode for the main app container
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      appContainerRef.current.requestFullscreen().catch((err) => {
        console.error(`Error attempting to enable fullscreen: ${err.message}`);
        alert(
          `Failed to enter fullscreen mode: ${err.message}. Your browser may not support fullscreen or it was blocked.`
        );
      });
    } else {
      document.exitFullscreen().catch((err) => {
        console.error(`Error attempting to exit fullscreen: ${err.message}`);
        alert(`Failed to exit fullscreen mode: ${err.message}.`);
      });
    }
  };

  // Remove a video
  const removeVideo = (videoId) => {
    setVideos((prev) => prev.filter((v) => v.id !== videoId));
  };

  // Update a video's position
  const updateVideoPosition = (videoId, newPosition) => {
    setVideos((prev) =>
      prev.map((v) => (v.id === videoId ? { ...v, position: newPosition } : v))
    );
  };

  // Update a video's size
  const updateVideoSize = (videoId, newSize) => {
    setVideos((prev) =>
      prev.map((v) => (v.id === videoId ? { ...v, size: newSize } : v))
    );
  };

  // Handle auto arrangement of videos vertically
  const handleAutoArrange = () => {
    if (videos.length === 0 || !appContainerRef.current) return;

    const parentRect = appContainerRef.current.getBoundingClientRect();
    const parentWidth = parentRect.width;
    const parentHeight = parentRect.height;
    const numVideos = videos.length;

    let arrangedVideos;

    if (isMobile) {
      // On mobile: stack videos full width
      const rowHeight = Math.max(parentHeight / numVideos, MIN_VIDEO_HEIGHT);
      let currentY = 0;

      arrangedVideos = videos.map((video) => {
        const videoWidth = parentWidth; // full width
        const videoHeight = rowHeight;

        const videoY = currentY;
        currentY += videoHeight;

        return {
          ...video,
          position: { x: 0, y: videoY },
          size: { width: videoWidth, height: videoHeight },
        };
      });
    } else {
      // Desktop: keep your existing arrangement logic
      const rowHeight = Math.max(parentHeight / numVideos, MIN_VIDEO_HEIGHT);
      const arrangeWidth = Math.max(
        MIN_VIDEO_WIDTH,
        Math.min(parentWidth * 0.7, parentWidth)
      );

      let currentY = 0;
      arrangedVideos = videos.map((video) => {
        const videoHeight = rowHeight;
        const videoWidth = arrangeWidth;
        const videoX = Math.max(0, (parentWidth - videoWidth) / 2);

        const videoY = currentY;
        currentY += videoHeight;

        return {
          ...video,
          position: { x: videoX, y: videoY },
          size: { width: videoWidth, height: videoHeight },
        };
      });
    }

    setVideos(arrangedVideos);
  };

  const handlePasteFromClipboard = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const url = (text || "").trim();

      if (!url) {
        alert("No URL found in clipboard.");
        return;
      }

      // Reflect in the input UI, but do not rely on this for the add
      setVideoUrl(url);

      // Use the value directly to avoid stale state on the first click
      addVideo(url);
    } catch (err) {
      console.error("Failed to read clipboard:", err);
      alert(
        "Could not access clipboard. Ensure the page is served over HTTPS and grant clipboard permission."
      );
    }
  };

  const handlePasteToInput = async () => {
    try {
      const text = await navigator.clipboard.readText();
      const url = (text || "").trim();

      if (!url) {
        alert("No URL found in clipboard.");
        return;
      }

      setVideoUrl(url);
    } catch (err) {
      console.error("Failed to read clipboard:", err);
      alert(
        "Could not access clipboard. Ensure the page is served over HTTPS and grant clipboard permission."
      );
    }
  };

  // Generate and share URL
  const handleShare = () => {
    const shareableVideos = videos.filter((video) => video.type !== "file");

    if (shareableVideos.length === 0) {
      alert("No shareable videos available (local files cannot be shared).");
      return;
    }

    const shareData = shareableVideos.map(({ url, type, name }) => ({
      url,
      type,
      name,
    }));

    try {
      const jsonString = JSON.stringify(shareData);
      const base64Data = btoa(jsonString);
      const shareUrl = `${window.location.origin}${window.location.pathname}?data=${base64Data}`;

      // Try modern clipboard API first
      if (navigator.clipboard && navigator.clipboard.writeText) {
        navigator.clipboard
          .writeText(shareUrl)
          .then(() => {
            setCopiedMessage("Link Copied!");
            setTimeout(() => setCopiedMessage(""), 3000);
          })
          .catch((err) => {
            console.error("Failed to copy URL:", err);
            // Fallback to creating a copyable text area
            createCopyFallback(shareUrl);
          });
      } else {
        // Fallback for browsers that don't support Clipboard API
        createCopyFallback(shareUrl);
      }
    } catch (e) {
      console.error("Failed to generate share URL:", e);
      alert("Failed to generate share URL.");
    }
  };

  // Add this new helper function
  const createCopyFallback = (text) => {
    // Create a temporary textarea element
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-999999px";
    textArea.style.top = "-999999px";
    document.body.appendChild(textArea);
    textArea.focus();
    textArea.select();

    try {
      const successful = document.execCommand("copy");
      if (successful) {
        setCopiedMessage("Link Copied!");
        setTimeout(() => setCopiedMessage(""), 3000);
      } else {
        // Last resort - show a modal with selectable text
        showCopyModal(text);
      }
    } catch (err) {
      console.error("Fallback copy failed:", err);
      showCopyModal(text);
    } finally {
      document.body.removeChild(textArea);
    }
  };

  // Add this modal function for final fallback
  const showCopyModal = (text) => {
    // Create modal overlay
    const modal = document.createElement("div");
    modal.style.cssText = `
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.8);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 1000;
  `;

    // Create modal content
    const content = document.createElement("div");
    content.style.cssText = `
    background: #1a1a1a;
    padding: 20px;
    border-radius: 8px;
    max-width: 90%;
    text-align: center;
  `;

    content.innerHTML = `
    <p style="color: white; margin-bottom: 15px;">Copy this link:</p>
    <textarea readonly style="
      width: 100%;
      height: 60px;
      background: #0a0a0a;
      color: white;
      border: 1px solid #333;
      border-radius: 4px;
      padding: 8px;
      font-family: monospace;
      resize: none;
    ">${text}</textarea>
    <button onclick="this.parentElement.parentElement.remove()" style="
      margin-top: 15px;
      background: #7c3aed;
      color: white;
      border: none;
      padding: 8px 16px;
      border-radius: 4px;
      cursor: pointer;
    ">Close</button>
  `;

    modal.appendChild(content);
    document.body.appendChild(modal);

    // Auto-select the text
    const textarea = content.querySelector("textarea");
    textarea.focus();
    textarea.select();

    // Close on click outside
    modal.addEventListener("click", (e) => {
      if (e.target === modal) {
        modal.remove();
      }
    });
  };

  return (
    <div
      ref={appContainerRef}
      className={`relative w-full h-screen bg-[#121212] overflow-hidden ${
        isFullscreen ? "fullscreen-active" : ""
      }`}
      style={{
        backgroundImage:
          "radial-gradient(circle at center, #1a1a1a 0%, #121212 100%)",
        position: "relative",
      }}
      onKeyDown={(e) => {
        if (e.key === "Escape") {
          if (theaterMode) setTheaterMode(false);
          if (document.fullscreenElement) document.exitFullscreen();
        }
      }}
      tabIndex={-1}
    >
      {/* Add CSS for resizing handles and the Copied message animation */}
      {/* Increased handle size and added background for better visibility on touch devices */}
      <style>
        {`
          /* Significantly increased handle size and added a more visible indicator */
          .react-resizable-handle.react-resizable-handle-se {
            width: 48px !important; /* Larger touch area for mobile */
            height: 48px !important; /* Larger touch area for mobile */
            background-color: rgba(139, 92, 246, 0.9) !important; /* More visible background */
            border-radius: 0 0 8px 0; /* Match video corner radius */
            cursor: se-resize !important; /* Ensure cursor is correct */
            z-index: 10; /* High z-index to be on top */
            display: flex; /* Use flexbox to center or align visual indicator */
            align-items: center; /* Center visual indicator vertically */
            justify-content: center; /* Center visual indicator horizontally */
             right: 0 !important; /* Ensure it's exactly in the corner */
             bottom: 0 !important; /* Ensure it's exactly in the corner */
          }
          /* Added a larger, high-contrast visual triangle inside the handle */
          .react-resizable-handle.react-resizable-handle-se::after {
              content: '';
              display: block;
              width: 16px; /* Even larger indicator */
              height: 16px; /* Even larger indicator */
              border-bottom: 3px solid white; /* Create a bold white triangle */
              border-right: 3px solid white;
              transform: rotate(45deg); /* Rotate to make it a triangle */
              opacity: 0.9; /* High visibility */
          }

          /* --- Corrected CSS for Theater Mode --- */
          .theater-mode-video {
              border: none !important; /* Remove border */
              box-shadow: none !important; /* Remove shadow */
              background: none !important; /* Remove background */
              cursor: default !important; /* Ensure cursor is default */
               /* Rnd manages position and size, no need for 100% overrides here */
          }

           /* Hide the Rnd handle in theater mode */
          .theater-mode-video .react-resizable-handle {
              display: none !important;
          }

          /* Hide the drag handle area in theater mode */
          .theater-mode-video .drag-handle {
              display: none !important;
          }
          /* --- End Corrected CSS --- */


           /* CSS for fade in/out animation for the "Copied!" message */
            @keyframes fadeInOut {
              0% { opacity: 0; transform: translate(-50%, -20px); }
              20% { opacity: 1; transform: translate(-50%, 0); }
              80% { opacity: 1; transform: translate(-50%, 0); }
              100% { opacity: 0; transform: translate(-50%, -20px); }
            }
            .animate-fadeInOut {
              animation: fadeInOut 3s ease-in-out forwards;
            }

            /* Ensure the close button has a higher z-index and is interactive */
            .close-button-no-drag {
                z-index: 20; /* Higher than the handle */
                position: relative; /* Ensure z-index is respected */
            }
          `}
      </style>

      {/* Theater mode back button */}
      {theaterMode && (
        <button
          onClick={toggleTheaterMode}
          className="absolute top-4 left-4 z-50 bg-[#0c0c0c] text-white p-3 rounded-full hover:bg-[#262626] transition-all duration-300 shadow-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
          style={{
            boxShadow: "0 4px 12px rgba(0, 0, 0, 0.3)",
            backdropFilter: "blur(10px)",
          }}
          aria-label="Exit theater mode"
        >
          <svg
            xmlns="http://www.w3.org/2000/svg"
            className="h-5 w-5"
            viewBox="0 0 20 20"
            fill="currentColor"
          >
            <path
              fillRule="evenodd"
              d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z"
              clipRule="evenodd"
            />
          </svg>
        </button>
      )}

      {/* Control panel - Hidden in theater mode */}
      {!theaterMode && videos.length > 0 && (
        <div className="absolute top-4 right-4 z-20 flex flex-col space-y-3">
          <button
            onClick={toggleInputPanel}
            className="bg-gradient-to-br from-[#7c3aed] to-[#6025c0] text-white p-3 rounded-full hover:from-[#8b5cf6] hover:to-[#7c3aed] transition-all duration-300 shadow-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            title={showInput ? "Hide input panel" : "Show input panel"}
            aria-label={showInput ? "Hide input panel" : "Show input panel"}
            style={{ boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)" }}
          >
            {showInput ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M5 12h14"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M12 4.5v15m7.5-7.5h-15"
                />
              </svg>
            )}
          </button>

          {/* paste from clipboard button */}
          {/* <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" stroke-width="1.5" stroke="currentColor" class="size-6">
  <path stroke-linecap="round" stroke-linejoin="round" d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184" />
</svg>
 */}

          <button
            onClick={handlePasteFromClipboard}
            className="bg-gradient-to-br from-[#7c3aed] to-[#6025c0] text-white p-3 rounded-full hover:from-[#8b5cf6] hover:to-[#7c3aed] transition-all duration-300 shadow-lg focus:outline-none focus:ring-2 focus:ring-purple-500"
            title="Paste from clipboard"
            aria-label="Paste from clipboard"
            style={{ boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              stroke-width="1.5"
              stroke="currentColor"
              class="size-6"
            >
              <path
                stroke-linecap="round"
                stroke-linejoin="round"
                d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
              />
            </svg>
          </button>

          {/* Auto Arrange Button */}
          {videos.length > 0 && (
            <button
              onClick={handleAutoArrange}
              className="bg-gradient-to-br from-[#f59e0b] to-[#d97706] text-white p-3 rounded-full hover:from-[#fbbf24] hover:to-[#f59e0b] transition-all duration-300 shadow-lg focus:outline-none focus:ring-2 focus:ring-amber-500"
              title="Auto arrange videos vertically"
              aria-label="Auto arrange videos vertically"
              style={{ boxShadow: "0 4px 12px rgba(245, 158, 11, 0.3)" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 12h16.5m-16.5 3.75h16.5M3.75 19.5h16.5M5.625 4.5h12.75c1.035 0 1.875.84 1.875 1.875v3.15c0 1.035-.84 1.875-1.875 1.875H5.625a1.875 1.875 0 0 1-1.875-1.875v-3.15c0-1.035.84-1.875 1.875-1.875Z"
                />
              </svg>
            </button>
          )}

          {/* Share Button */}
          {videos.length > 0 && (
            <button
              onClick={handleShare}
              className="bg-gradient-to-br from-[#0ea5e9] to-[#0284c7] text-white p-3 rounded-full hover:from-[#38bdf8] hover:to-[#0ea5e9] transition-all duration-300 shadow-lg focus:outline-none focus:ring-2 focus:ring-sky-500 relative"
              title="Share current videos"
              aria-label="Share current videos"
              style={{ boxShadow: "0 4px 12px rgba(14, 165, 233, 0.3)" }}
            >
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke-width="1.5"
                stroke="currentColor"
                class="size-6"
              >
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  d="M7.217 10.907a2.25 2.25 0 1 0 0 2.186m0-2.186c.18.324.283.696.283 1.093s-.103.77-.283 1.093m0-2.186 9.566-5.314m-9.566 7.5 9.566 5.314m0 0a2.25 2.25 0 1 0 3.935 2.186 2.25 2.25 0 0 0-3.935-2.186Zm0-12.814a2.25 2.25 0 1 0 3.933-2.185 2.25 2.25 0 0 0-3.933 2.185Z"
                />
              </svg>

              {copiedMessage && (
                <span className="absolute top-full left-1/2 transform -translate-x-50% mt-2 px-2 py-1 bg-gray-800 text-white text-xs rounded-md whitespace-nowrap animate-fadeInOut">
                  {copiedMessage}
                </span>
              )}
            </button>
          )}

          <button
            onClick={toggleTheaterMode}
            className="bg-gradient-to-br from-[#3b82f6] to-[#1e40af] text-white p-3 rounded-full hover:from-[#60a5fa] hover:to-[#3b82f6] transition-all duration-300 shadow-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            title="Theater mode"
            aria-label="Toggle theater mode"
            style={{ boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)" }}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M6 6.878V6a2.25 2.25 0 0 1 2.25-2.25h7.5A2.25 2.25 0 0 1 18 6v.878m-12 0c.235-.083.487-.128.75-.128h10.5c.263 0 .515.045.75.128m-12 0A2.25 2.25 0 0 0 4.5 9v.878m13.5-3A2.25 2.25 0 0 1 19.5 9v.878m0 0a2.246 2.246 0 0 0-.75-.128H5.25c-.263 0-.515.045-.75.128m15 0A2.25 2.25 0 0 1 21 12v6a2.25 2.25 0 0 1-2.25 2.25H5.25A2.25 2.25 0 0 1 3 18v-6c0-.98.626-1.813 1.5-2.122"
              />
            </svg>
          </button>
          <button
            onClick={toggleFullscreen}
            className="bg-gradient-to-br from-[#10b981] to-[#059669] text-white p-3 rounded-full hover:from-[#34d399] hover:to-[#10b981] transition-all duration-300 shadow-lg focus:outline-none focus:ring-2 focus:ring-green-500"
            title={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
            style={{ boxShadow: "0 4px 12px rgba(16, 185, 129, 0.3)" }}
          >
            {isFullscreen ? (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M9 9V4.5M9 9H4.5M9 9 3.75 3.75M9 15v4.5M9 15H4.5M9 15l-5.25 5.25M15 9h4.5M15 9V4.5M15 9l5.25-5.25M15 15h4.5M15 15v4.5m0-4.5 5.25 5.25"
                />
              </svg>
            ) : (
              <svg
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                strokeWidth="1.5"
                stroke="currentColor"
                className="size-6"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  d="M3.75 3.75v4.5m0-4.5h4.5m-4.5 0L9 9M3.75 20.25v-4.5m0 4.5h4.5m-4.5 0L9 15M20.25 3.75h-4.5m4.5 0v4.5m0-4.5L15 9m5.25 11.25h-4.5m4.5 0v-4.5m0 4.5L15 15"
                />
              </svg>
            )}
          </button>
        </div>
      )}

      {/* Input panel */}
      {showInput && !theaterMode && (
        <div
          className="absolute top-4 left-4 w-[340px] bg-[#0a0a0a] bg-opacity-95 p-5 rounded-xl shadow-2xl z-20 border border-[#333333]"
          style={{
            backdropFilter: "blur(10px)",
            boxShadow: "0 10px 25px rgba(0, 0, 0, 0.5)",
          }}
        >
          <h2 className="text-white text-lg mb-4 font-medium">Add Video</h2>

          {/* URL input */}
          <div className="mb-4 relative">
            <div className="relative">
              <input
                type="text"
                value={videoUrl}
                onChange={(e) => setVideoUrl(e.target.value)}
                onKeyPress={(e) => e.key === "Enter" && addVideo()}
                placeholder="Paste video URL..."
                className="w-full p-3 bg-[#1a1a1a] text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-purple-500 border border-[#333333] transition-all duration-300 placeholder-gray-500"
                aria-label="Video URL input"
              />
              {videoUrl && (
                <button
                  onClick={() => setVideoUrl("")}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  aria-label="Clear URL input"
                >
                  ×
                </button>
              )}
              {!videoUrl && (
                <button
                  onClick={handlePasteToInput}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-white transition-colors"
                  aria-label="Clear URL input"
                >
                  <svg
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke-width="1.5"
                    stroke="currentColor"
                    class="size-6"
                  >
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      d="M15.666 3.888A2.25 2.25 0 0 0 13.5 2.25h-3c-1.03 0-1.9.693-2.166 1.638m7.332 0c.055.194.084.4.084.612v0a.75.75 0 0 1-.75.75H9a.75.75 0 0 1-.75-.75v0c0-.212.03-.418.084-.612m7.332 0c.646.049 1.288.11 1.927.184 1.1.128 1.907 1.077 1.907 2.185V19.5a2.25 2.25 0 0 1-2.25 2.25H6.75A2.25 2.25 0 0 1 4.5 19.5V6.257c0-1.108.806-2.057 1.907-2.185a48.208 48.208 0 0 1 1.927-.184"
                    />
                  </svg>
                </button>
              )}
            </div>
          </div>

          <div className="flex space-x-3">
            <button
              onClick={addVideo}
              className="flex-1 bg-gradient-to-r from-[#7c3aed] to-[#6025c0] text-white py-2.5 px-4 rounded-lg hover:from-[#8b5cf6] hover:to-[#7c3aed] transition-all duration-300 shadow-md font-medium focus:outline-none focus:ring-2 focus:ring-purple-500 disabled:opacity-50 disabled:cursor-not-allowed"
              style={{ boxShadow: "0 4px 12px rgba(124, 58, 237, 0.3)" }}
              disabled={!videoUrl.trim()}
              aria-label="Add video from URL"
            >
              <div className="flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zm1-11a1 1 0 10-2 0v2H7a1 1 0 100 2h2v2a1 1 0 102 0v-2h2a1 1 0 100-2h-2V7z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Add URL</span>
              </div>
            </button>

            <button
              onClick={() => fileInputRef.current.click()}
              className="flex-1 bg-gradient-to-r from-[#3b82f6] to-[#1e40af] text-white py-2.5 px-4 rounded-lg hover:from-[#60a5fa] hover:to-[#3b82f6] transition-all duration-300 shadow-md font-medium focus:outline-none focus:ring-2 focus:ring-blue-500"
              style={{ boxShadow: "0 4px 12px rgba(59, 130, 246, 0.3)" }}
              aria-label="Upload local video"
            >
              <div className="flex items-center justify-center">
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className="h-4 w-4 mr-2"
                  viewBox="0 0 20 20"
                  fill="currentColor"
                >
                  <path
                    fillRule="evenodd"
                    d="M3 17a1 1 0 011-1h12a1 1 0 110 2H4a1 1 0 01-1-1zM6.293 6.707a1 1 0 010-1.414l3-3a1 1 0 011.414 0l3 3a1 1 0 01-1.414 1.414L11 5.414V13a1 1 0 11-2 0V5.414L7.707 6.707a1 1 0 01-1.414 0z"
                    clipRule="evenodd"
                  />
                </svg>
                <span>Upload</span>
              </div>
            </button>

            <input
              ref={fileInputRef}
              type="file"
              accept="video/*"
              multiple
              onChange={handleFileChange}
              className="hidden"
              aria-hidden="true"
            />
          </div>
          <p className="text-xs text-gray-400 pt-4">
            Disclaimer: Videos are not uploaded to any server. They are simply
            loaded in the browser. External URLs may not always allow embedding.
          </p>
          <p className="text-xs text-gray-400 pt-2">
            Local files cannot be shared via URL.
          </p>
        </div>
      )}

      {/* Video containers */}
      <div
        className={`relative w-full h-full ${theaterMode ? "bg-black" : ""}`}
        ref={appContainerRef}
      >
        {videos.map((video) => (
          <VideoComponent
            key={video.id}
            video={video}
            onRemove={removeVideo}
            updateVideoPosition={updateVideoPosition}
            updateVideoSize={updateVideoSize}
            theaterMode={theaterMode}
          />
        ))}

        {/* Message when no videos are added and input is hidden */}
        {videos.length === 0 && !showInput && (
          <div className="absolute inset-0 flex flex-col items-center justify-center text-gray-500">
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth="1.5"
              stroke="currentColor"
              className="size-16 mb-4 opacity-70"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5m-13.5-9L12 3m0 0 4.5 4.5M12 3v13.5"
              />
            </svg>
            <p className="text-lg font-medium">No videos added yet</p>
            <p className="text-sm mt-1">
              Click the '+' button to add a video from URL or upload.
            </p>
            <button
              onClick={toggleInputPanel}
              className="mt-4 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              Add Videos
            </button>
            {/* paste button */}
            <button
              onClick={handlePasteFromClipboard}
              className="mt-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-purple-500"
            >
              Paste Video URL
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

// Individual video component using react-rnd
const VideoComponent = ({
  video,
  onRemove,
  updateVideoPosition,
  updateVideoSize,
  theaterMode,
}) => {
  const videoRef = useRef(null);

  const renderVideoContent = () => {
    if (video.type === "file" || video.type === "direct") {
      return (
        <video
          ref={videoRef}
          src={video.url}
          controls={true} // Hide controls in theater mode
          className="w-full h-full bg-black object-contain"
          onError={(e) => console.error("Video error:", e)}
          // Autoplay only in theater mode? Let's keep autoplay only when controls are shown unless the embed has autoplay
          autoPlay={!theaterMode}
          loop // Loop in theater mode?
          // Removed muted={theaterMode} to fix audio
        >
          Your browser does not support the video tag.
        </video>
      );
    } else {
      // For iframes, autoplay is handled by the embed URL params like ?autoplay=1
      return (
        <iframe
          key={video.id}
          title={`Video ${video.name}`}
          src={video.url} // don’t change URL based on theaterMode
          className="w-full h-full bg-black"
          allowFullScreen
          frameBorder="0"
          sandbox="allow-same-origin allow-forms allow-popups allow-scripts allow-presentation"
          allow="autoplay; fullscreen; accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture; presentation"
        />
      );
    }
  };

  return (
    <Rnd
      position={{ x: video.position.x, y: video.position.y }}
      size={{ width: video.size.width, height: video.size.height }}
      minWidth={MIN_VIDEO_WIDTH}
      minHeight={MIN_VIDEO_HEIGHT}
      bounds="parent"
      enableResizing={{
        bottomRight: !theaterMode, // Enable bottom-right resize handle when not in theater mode
      }}
      enableDragging={!theaterMode} // Disable dragging in theater mode
      dragHandleClassName="drag-handle"
      // Use the 'cancel' prop to prevent dragging when clicking the close button
      cancel=".close-button-no-drag"
      onDragStop={(e, d) => {
        updateVideoPosition(video.id, { x: d.x, y: d.y });
      }}
      onResizeStop={(e, direction, ref, delta, position) => {
        updateVideoSize(video.id, {
          width: ref.offsetWidth,
          height: ref.offsetHeight,
        });
        updateVideoPosition(video.id, { x: position.x, y: position.y });
      }}
      className={`flex flex-col ${
        theaterMode
          ? "theater-mode-video" // Apply theater mode styling class
          : "rounded-lg overflow-hidden shadow-xl transition-shadow duration-300"
      }`}
      style={{
        zIndex: 1,
        // Rnd handles position, width, height. We add other styles.
        boxShadow: !theaterMode ? "0 10px 25px rgba(0, 0, 0, 0.5)" : "none",
        border: !theaterMode ? "1px solid rgba(255, 255, 255, 0.1)" : "none",
        background: "black", // Keep background black for the video area
        display: "flex",
        flexDirection: "column",
        // Rnd's position and size props control the layout in both modes
      }}
    >
      {/* Header with video name and close button - Hidden in theater mode */}
      {!theaterMode && (
        <div
          className="drag-handle bg-[#0c0c0c] px-2 py-1 cursor-move flex justify-between items-center text-white text-xs select-none"
          style={{ backdropFilter: "blur(10px)", flexShrink: 0 }}
        >
          <div className="flex-grow truncate text-gray-400 hover:text-white transition-colors duration-200 mr-2">
            {video.name}
          </div>
          <button
            // Add the class for the 'cancel' prop
            className="close-button-no-drag text-gray-500 hover:text-red-500 transition-colors p-1 focus:outline-none"
            onClick={() => onRemove(video.id)} // Simple click handler without stopPropagation
            aria-label={`Remove video ${video.name}`}
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              className="h-3 w-3"
              viewBox="0 0 20 20"
              fill="currentColor"
            >
              <path
                fillRule="evenodd"
                d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z"
                clipRule="evenodd"
              />
            </svg>
          </button>
        </div>
      )}

      {/* Video content container */}
      <div
        // Ensure the video content fills the remaining space in the Rnd container
        className={`relative w-full ${theaterMode ? "h-full" : "flex-grow"}`}
      >
        {renderVideoContent()}
      </div>
    </Rnd>
  );
};

export default MultiVideoPlayer;
